import { SupabaseClient } from "@supabase/supabase-js";

import { CO_POC_COLUMN, orderEventPocIds, withCoPocColumnFallback } from "@/lib/events/event-pocs";
import { isUndefinedColumnError } from "@/lib/utils/postgrest";

// Single source of truth for Slack text escaping lives in the dependency-free slack-text
// module (safe to import from client code). Re-exported here so existing callers that pull
// it alongside the mention helpers keep working.
export { escapeSlackText } from "./slack-text";

const FALLBACK_MENTION = "<!channel>";

/** Josh's Slack user ID — the RFP data-hygiene point of contact (manual submissions, missing venue emails). */
export const JOSH_SLACK_MENTION = "<@U08SF7ALG49>";

/**
 * Slack mentions for the event's POCs (the lead who claimed it, then any co-POCs), space-joined, or
 * `<!channel>` fallback.
 *
 * @param supabase - Service-role client or admin session. RLS on
 *   `events_admin_data` blocks the `authenticated` role.
 * @param eventId - nowadays.events.event_id
 */
export async function getEventPocSlackMention(supabase: SupabaseClient, eventId: number): Promise<string> {
  // Thin wrapper over getEventPoc — the mention is identical on every path; this drops the
  // `claimed` flag for callers that only need whom to ping.
  const { mention } = await getEventPoc(supabase, eventId);
  return mention;
}

/** The POC columns read off `events_admin_data`; `co_poc_user_ids` is absent until its migration is applied. */
interface EventPocColumns {
  claimed_by_user_id: number | null;
  co_poc_user_ids?: number[] | null;
}

/**
 * Space-joined `<@U…>` mentions for the given POC ids, in order, skipping anyone without a Slack
 * user id — or `<!channel>` when none of them has one.
 *
 * @param pocIds - Ordered POC user ids (lead first).
 * @param slackByUser - `admin_users.user_id` → `slack_user_id`.
 * @returns The mention string.
 */
function formatPocMentions(pocIds: number[], slackByUser: Map<number, string | null>): string {
  const slackIds = [...new Set(pocIds.map((id) => slackByUser.get(id)).filter((id): id is string => Boolean(id)))];
  return slackIds.length > 0 ? slackIds.map((id) => `<@${id}>`).join(" ") : FALLBACK_MENTION;
}

/**
 * Resolves the ops POCs assigned to the enterprise org behind an event's legacy `orgs.org_id` —
 * `enterprise_organizations.org_owner_user_ids` (set via admin-dash-v2's org "Ops POCs" picker),
 * reached through the `org_enterprise_config` bridge. An account's ops POC should hear about every
 * event under that org, not only ones they personally claimed as the event's own POC. Mirrors
 * `admin-event-header.ts`'s `loadEnterpriseOrg` resolution (duplicate bridge rows exist in prod per
 * ENG-789, so this takes the lowest `id`). Best-effort: any read failure, or no org / no bridge / no
 * enterprise org / no owners set, returns `[]` — this must never break the mention it feeds into.
 *
 * @param supabase - Service-role client (RLS on `enterprise_organizations` blocks `authenticated`).
 * @param eventId - nowadays.events.event_id
 * @returns The org's ops-POC user ids (`nowadays.users.user_id`), or `[]`.
 */
async function getEnterpriseOrgOpsPocIds(supabase: SupabaseClient, eventId: number): Promise<number[]> {
  const { data: eventRow, error: eventError } = await supabase
    .schema("nowadays")
    .from("events")
    .select("org_id")
    .eq("event_id", eventId)
    .maybeSingle();
  if (eventError || !eventRow?.org_id) return [];

  const { data: configRows, error: configError } = await supabase
    .schema("nowadays")
    .from("org_enterprise_config")
    .select("enterprise_organization_id")
    .eq("org_id", eventRow.org_id)
    .order("id", { ascending: true })
    .limit(1);
  if (configError || !configRows?.[0]) return [];

  const { data: orgRow, error: orgError } = await supabase
    .schema("nowadays")
    .from("enterprise_organizations")
    .select("org_owner_user_ids")
    .eq("id", configRows[0].enterprise_organization_id)
    .maybeSingle();

  if (orgError) {
    if (!isUndefinedColumnError(orgError, "org_owner_user_ids")) {
      console.error("getEnterpriseOrgOpsPocIds: failed to read enterprise_organizations", { eventId }, orgError);
    }
    return [];
  }

  return orgRow?.org_owner_user_ids ?? [];
}

/**
 * Like {@link getEventPocSlackMention}, but also reports whether the event is actually claimed —
 * so a card can gate its "Claim on Fetch" link on `!claimed` instead of string-matching the
 * `<!channel>` fallback (which {@link getEventPocSlackMention} also returns on a DB error, i.e. a
 * *claimed* event would wrongly show the claim link). `claimed` is true when the event has any POC
 * (lead or co-POC) — it reflects the EVENT's own claim state only, never the enterprise org's ops
 * POC(s) merged into the mention below. Every error/unclaimed-and-ownerless path returns
 * `{ mention: <!channel>, claimed: false }`.
 *
 * The mention also always includes the enterprise org's ops POC(s) (see
 * {@link getEnterpriseOrgOpsPocIds}), if any are set, even when they aren't the event's own POC —
 * an account's ops POC should hear about every event under that org.
 *
 * @param supabase - Service-role client or admin session. RLS on `events_admin_data` blocks the
 *   `authenticated` role.
 * @param eventId - nowadays.events.event_id
 * @returns Every POC's mention (live `<@U…>`s, space-joined, event POCs first then org ops POCs —
 *   or `<!channel>`) and whether the event is claimed.
 */
export async function getEventPoc(
  supabase: SupabaseClient,
  eventId: number
): Promise<{ mention: string; claimed: boolean }> {
  const { data: adminData, error: adminError } = await withCoPocColumnFallback("getEventPoc", (includeCoPocs) =>
    supabase
      .schema("nowadays")
      .from("events_admin_data")
      .select(includeCoPocs ? `claimed_by_user_id, ${CO_POC_COLUMN}` : "claimed_by_user_id")
      .eq("event_id", eventId)
      .maybeSingle<EventPocColumns>()
  );

  if (adminError) {
    console.error("getEventPoc: failed to read events_admin_data", { eventId }, adminError);
    return { mention: FALLBACK_MENTION, claimed: false };
  }
  const pocIds = orderEventPocIds(adminData?.claimed_by_user_id, adminData?.co_poc_user_ids);
  const claimed = pocIds.length > 0;

  // Sequential (not Promise.all'd with the read above) so a mocked test double's positional
  // response queue stays deterministic — this path is best-effort and not latency-sensitive.
  const orgOpsPocIds = await getEnterpriseOrgOpsPocIds(supabase, eventId);

  const allIds = [...new Set([...pocIds, ...orgOpsPocIds])];
  if (allIds.length === 0) return { mention: FALLBACK_MENTION, claimed };

  const { data: pocRows, error: pocError } = await supabase
    .schema("nowadays")
    .from("admin_users")
    .select("user_id, slack_user_id")
    .in("user_id", allIds);

  if (pocError) {
    console.error("getEventPoc: failed to read admin_users", { eventId, allIds }, pocError);
    return { mention: FALLBACK_MENTION, claimed };
  }

  const slackByUser = new Map<number, string | null>();
  (pocRows ?? []).forEach((row) => slackByUser.set(row.user_id, row.slack_user_id));
  return { mention: formatPocMentions(allIds, slackByUser), claimed };
}

/**
 * Slack mention for the VA assigned to the event (`events_admin_data.va_user_id`), or `<!channel>`
 * fallback when the event has no VA or the VA has no `slack_user_id`. Mirrors
 * {@link getEventPocSlackMention} but on the VA slot — used to route data-hygiene alerts (missing
 * venue email / manual RFP) to whoever actually owns the code (ENG-303).
 *
 * @param supabase - Service-role client or admin session. RLS on `events_admin_data` blocks the
 *   `authenticated` role.
 * @param eventId - nowadays.events.event_id
 */
export async function getEventVaSlackMention(supabase: SupabaseClient, eventId: number): Promise<string> {
  const { data: adminData, error: adminError } = await supabase
    .schema("nowadays")
    .from("events_admin_data")
    .select("va_user_id")
    .eq("event_id", eventId)
    .maybeSingle();

  if (adminError) {
    console.error("getEventVaSlackMention: failed to read events_admin_data", { eventId }, adminError);
    return FALLBACK_MENTION;
  }
  if (!adminData?.va_user_id) return FALLBACK_MENTION;

  const { data: vaData, error: vaError } = await supabase
    .schema("nowadays")
    .from("admin_users")
    .select("slack_user_id")
    .eq("user_id", adminData.va_user_id)
    .maybeSingle();

  if (vaError) {
    console.error(
      "getEventVaSlackMention: failed to read admin_users",
      { eventId, vaUserId: adminData.va_user_id },
      vaError
    );
    return FALLBACK_MENTION;
  }

  return vaData?.slack_user_id ? `<@${vaData.slack_user_id}>` : FALLBACK_MENTION;
}

/**
 * Batched form of {@link getEventPocSlackMention}: resolves POC mentions for many
 * events in two queries (one `events_admin_data`, one `admin_users`) instead of two
 * per event. Every requested id is present in the returned map; ids with no POC,
 * or whose POCs have no `slack_user_id`, map to `<!channel>`.
 *
 * @param supabase - Service-role client or admin session (RLS blocks `authenticated`).
 * @param eventIds - nowadays.events.event_id values to resolve.
 * @returns Map of event_id → Slack mention string (space-joined when the event has several POCs).
 */
export async function getEventPocSlackMentions(
  supabase: SupabaseClient,
  eventIds: number[]
): Promise<Map<number, string>> {
  const mentions = new Map<number, string>();
  if (eventIds.length === 0) return mentions;

  const { data: adminRows, error: adminError } = await withCoPocColumnFallback(
    "getEventPocSlackMentions",
    (includeCoPocs) =>
      supabase
        .schema("nowadays")
        .from("events_admin_data")
        .select(includeCoPocs ? `event_id, claimed_by_user_id, ${CO_POC_COLUMN}` : "event_id, claimed_by_user_id")
        .in("event_id", eventIds)
        .returns<(EventPocColumns & { event_id: number })[]>()
  );

  if (adminError) {
    console.error("getEventPocSlackMentions: failed to read events_admin_data", adminError);
    eventIds.forEach((id) => mentions.set(id, FALLBACK_MENTION));
    return mentions;
  }

  const pocIdsByEvent = new Map<number, number[]>();
  (adminRows ?? []).forEach((r) =>
    pocIdsByEvent.set(r.event_id, orderEventPocIds(r.claimed_by_user_id, r.co_poc_user_ids))
  );

  const pocUserIds = Array.from(new Set([...pocIdsByEvent.values()].flat()));

  const slackByUser = new Map<number, string | null>();
  if (pocUserIds.length > 0) {
    const { data: pocs, error: pocsError } = await supabase
      .schema("nowadays")
      .from("admin_users")
      .select("user_id, slack_user_id")
      .in("user_id", pocUserIds);
    if (pocsError) {
      console.error("getEventPocSlackMentions: failed to read admin_users", pocsError);
    } else {
      (pocs ?? []).forEach((o) => slackByUser.set(o.user_id, o.slack_user_id));
    }
  }

  for (const id of eventIds) {
    mentions.set(id, formatPocMentions(pocIdsByEvent.get(id) ?? [], slackByUser));
  }
  return mentions;
}

/**
 * Space-joined Slack mentions for every admin with `northstar_poc = true`,
 * or `<!channel>` fallback when none have a `slack_user_id`.
 */
export async function getNorthstarPocsSlackMentions(supabase: SupabaseClient): Promise<string> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("admins")
    .select("slack_user_id")
    .eq("northstar_poc", true);

  if (error) {
    console.error("getNorthstarPocsSlackMentions: failed to read admins", error);
    return FALLBACK_MENTION;
  }

  const ids = (data ?? []).map((row) => row.slack_user_id).filter((id): id is string => Boolean(id));

  return ids.length > 0 ? ids.map((id) => `<@${id}>`).join(" ") : FALLBACK_MENTION;
}
